/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useInitSquare } from 'hooks';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useCallbackWithLoader } from '../../hooks/useCallbackWithLoader';
import { showMessage } from '../../store/slices/message.slice';
import { Message } from '../index';
import { LoaderButton } from '../ProgressButton';
import PaymentErrors from './PaymentErrors';
import { styles } from './styles';
import { tokenize } from './utils';

const UserPaymentForm = ({ plans, isRenewForm = false, onSubmitHandler, setLoading }) => {
	const { control, handleSubmit, getValues } = useFormContext();
	const dispatch = useDispatch();

	const onInitCard = useCallback(() => setLoading(false), [setLoading]);

	const { createPayment, paymentMethod, cardErrors } = useInitSquare({
		onInitializeCard: onInitCard
	});

	const handleProceedPayment =
		({ payMethod }) =>
		async () => {
			const { token, errors } = await tokenize(payMethod);

			if (errors) return;

			const formData = getValues();

			try {
				return await createPayment({
					token,
					paymentHandler: onSubmitHandler,
					paymentData: {
						plan_type: formData.plan_type,
						card_on_file: formData.save_card_information,
						fname: formData.first_name,
						lname: formData.last_name,
						...(isRenewForm && { email: formData.email })
					}
				});
			} catch (e) {
				console.error('PAYMENT FAILED: ', e);
				dispatch(
					showMessage({
						message: 'Payment Failed! Please, try again later',
						variant: 'error'
					})
				);
			}
		};

	const { callback: onSubmit, loading } = useCallbackWithLoader(
		handleProceedPayment({ payMethod: paymentMethod, shouldVerify: true })
	);

	return (
		<form id="payment-form" onSubmit={handleSubmit(onSubmit)}>
			<Container component="main" maxWidth="lg" sx={styles.container} id="form-container">
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Controller
							name="plan_type"
							control={control}
							render={({ field, fieldState: { error } }) => (
								<FormControl variant="filled" error={Boolean(error)} sx={styles.field}>
									<InputLabel id="select-plan-type" required>
										{isRenewForm ? 'Select Renewal Member Type' : 'Plan Type'}
									</InputLabel>
									<Select labelId="select-plan-type" id="demo-simple-select-filled" {...field}>
										{plans.map(plan => {
											return (
												<MenuItem key={plan.id} value={plan.id}>
													{plan.name}
												</MenuItem>
											);
										})}
									</Select>
									{Boolean(error?.message) && <Box sx={styles.error}>{error.message}</Box>}
								</FormControl>
							)}
						/>

						<FormControl variant="filled" sx={styles.field}>
							<Controller
								name="first_name"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<TextField
										{...field}
										variant="filled"
										label="First Name"
										error={Boolean(error)}
										helperText={error?.message}
									/>
								)}
							/>
						</FormControl>

						<FormControl variant="filled" sx={styles.field}>
							<Controller
								name="last_name"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<TextField
										{...field}
										variant="filled"
										label="Last Name"
										error={Boolean(error)}
										helperText={error?.message}
									/>
								)}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={6}>
						<FormControl variant="filled" sx={styles.field}>
							<Controller
								name="email"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<TextField
										{...field}
										variant="filled"
										label="Email Address"
										error={Boolean(error)}
										helperText={error?.message}
										inputProps={{ readOnly: !isRenewForm }}
									/>
								)}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name="save_card_information"
							control={control}
							render={({ field }) => (
								<FormControlLabel
									control={<Checkbox {...field} defaultChecked />}
									label="Save Card Information"
								/>
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<Box id="card-container"></Box>
					</Grid>
					<Grid item xs={12}>
						<PaymentErrors errors={cardErrors} />
					</Grid>
					<LoaderButton
						type="submit"
						variant="contained"
						color="primary"
						circleColor="secondary"
						loading={loading}
					>
						{isRenewForm ? 'Process Payment' : 'Subscribe to MemberCenter'}
					</LoaderButton>
					<Message />
				</Grid>
			</Container>
		</form>
	);
};

export default UserPaymentForm;
